import { useRef } from "react";
import styles from "./styles.module.scss";
import { Check, ChevronDown, ChevronUp, Medal, Paperclip } from "lucide-react";
import { Tag } from "ui-kit-v2/src/tag/tag";
import { Button } from "ui-kit-v2/src/button/button";
import { _ } from "i18n";
import { CoachReviewsAIGenerateButton } from "../../AIGenerateButton";
import { useCoachReviewsStore, getDraftKey } from "common/src/gamePage/stores/coach-reviews.store";
import { useEditStore } from "common/src/gamePage/edit-store";
import { useAttachStore } from "common/src/gamePage/attach-store";
import { AICoachReviewsMode, AttachModeKind, PlayerBadgesModalMode } from "common/src/gamePage/types";
import { CoachReviewsSendButton } from "../../SendButton";
import { useActiveCoachReviewsContext } from "common/src/gamePage/hooks/useActiveCoachReviewsContext";
import { CoachReviewsStatusTag } from "../../StatusTag";
import { CoachReviewsAttachedEvents } from "../../AttachedEvents";
import { CoachReviewsAIGeneratedIcon } from "../../AIGeneratedIcon";
import { useAttachedEvents } from "common/src/gamePage/hooks/useAttachedEvents";
import { Tooltip } from "ui-kit-v2/src/tooltip/tooltip";
import { CoachReviewsRatingTag } from "../../RatingTag";
import { translatePosition } from "common/src/players-grid/utils";

interface CoachReviewsPlayerProps {
	playerId: number;
	rating: number | null;
	name: string;
	jerseyNumber: number | null;
	position: string | null;
	hasParents: boolean;
	sentReviewText: string | null;
	sentEventIds: number[] | null;
	aiGenerationAvailable: boolean;
}

export const CoachReviewsPlayer = ({
	playerId,
	rating,
	name,
	jerseyNumber,
	position,
	hasParents,
	sentReviewText,
	sentEventIds,
	aiGenerationAvailable,
}: CoachReviewsPlayerProps) => {
	const { gameId, userId } = useActiveCoachReviewsContext();
	const { setPlayerBadgesModal } = useEditStore();
	const { enterAttachMode, setAttachedEvents } = useAttachStore();
	const draftKey = getDraftKey({ kind: "player", userId: userId, gameId: gameId, playerId: playerId });
	const draft = useCoachReviewsStore((state) => state.drafts[draftKey]);
	const upsertDraft = useCoachReviewsStore((state) => state.upsertDraft);
	const isOpen = useCoachReviewsStore((state) => state.toggleState.playerKey === draftKey);
	const toggleOpen = useCoachReviewsStore((state) => state.toggleOpen);
	const containerRef = useRef<HTMLDivElement>(null);

	const wasReviewSent = Boolean(sentReviewText);
	const textAreaValue = wasReviewSent ? sentReviewText || "" : draft?.reviewText || "";
	const attachedEvents = useAttachedEvents(wasReviewSent ? sentEventIds : draft?.attachedEventIds);
	const hasReviewText = Boolean(draft?.reviewText);
	const hasAttachedEvents = Boolean(draft?.attachedEventIds?.length || sentEventIds?.length);
	const hasAssignedBadge = Boolean(draft?.kind === "player" && draft?.assignedBadgeId);
	const shouldShowBadge = hasReviewText || hasAttachedEvents || hasAssignedBadge || wasReviewSent;

	const handleToggle = () => {
		toggleOpen({ kind: "player", userId: userId, gameId: gameId, playerId: playerId });
	};

	const handleReviewTextChange = (reviewText: string) => {
		// Clearing AI-generated text resets the AI flag and drops the linked AI content id
		const shouldResetAIFlag = draft?.generatedWithAI && reviewText === "";

		upsertDraft({
			kind: "player",
			userId,
			gameId,
			playerId,
			reviewText,
			...(shouldResetAIFlag && { generatedWithAI: false, contentId: null }),
		});
	};

	const handleAttachEvents = () => {
		// Prefil saved attached events for the player if any exist in the draft
		setAttachedEvents(
			{
				kind: AttachModeKind.Player,
				playerId: playerId,
				name: name,
			},
			attachedEvents
		);

		// Enter attach mode for the player
		enterAttachMode({
			kind: AttachModeKind.Player,
			playerId: playerId,
			name: name,
		});
	};

	const handleAttachBadge = () => {
		const existingBadgeId = draft?.kind === "player" ? draft?.assignedBadgeId : null;

		setPlayerBadgesModal({
			show: true,
			id: playerId,
			name: name,
			badgeIds: existingBadgeId ? [existingBadgeId] : [],
			mode: PlayerBadgesModalMode.Attach,
		});
	};

	return (
		<div className={styles.player}>
			<button type="button" className={styles["player__header"]} onClick={handleToggle}>
				<div className={styles["player__header-left"]}>
					{rating !== null && <CoachReviewsRatingTag rating={rating} position={position} />}
					<p className={styles["player__name"]}>{name}</p>
					{jerseyNumber !== null && position !== null && (
						<Tag variant="outline" palette="neutral">
							#{jerseyNumber} {translatePosition(position)}
						</Tag>
					)}
					{shouldShowBadge && <CoachReviewsStatusTag status={wasReviewSent ? "sent" : "draft"} />}
					{draft?.generatedWithAI && <CoachReviewsAIGeneratedIcon />}
				</div>
				{isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
			</button>
			{isOpen && (
				<div className={styles["player__body"]}>
					<textarea
						className={styles["player__textarea"]}
						placeholder={_("game.coachReviews.player.textarea.placeholder")}
						value={textAreaValue}
						onChange={(e) => handleReviewTextChange(e.target.value)}
						disabled={wasReviewSent}
					/>
					{!wasReviewSent && (
						<>
							<div className={styles["player__actions"]} ref={containerRef}>
								{aiGenerationAvailable && (
									<CoachReviewsAIGenerateButton
										mode={AICoachReviewsMode.Player}
										playerId={playerId}
										compact
										popoverWidthRef={containerRef}
									/>
								)}
								<Tooltip label={_("game.coachReviews.attachBadge.tooltip")} position="bottom">
									<Button
										type="button"
										size="small"
										variant="outline"
										className="w-fit"
										iconLeft={
											<div className={styles["player__badge-icons"]}>
												<Medal size={16} />
												{hasAssignedBadge && <Check size={16} className={styles["player__badge-icon--assigned"]} />}
											</div>
										}
										onClick={handleAttachBadge}
									/>
								</Tooltip>
								{!hasAttachedEvents && (
									<Button
										type="button"
										size="small"
										variant="neutral"
										iconLeft={<Paperclip size={16} />}
										onClick={handleAttachEvents}
									>
										{_("game.coachReviews.attachButton.label")}
									</Button>
								)}
							</div>
						</>
					)}
					{hasAttachedEvents && (
						<CoachReviewsAttachedEvents events={attachedEvents} onEditClick={handleAttachEvents} canEdit={!wasReviewSent} />
					)}
					<CoachReviewsSendButton
						mode={AICoachReviewsMode.Player}
						playerId={playerId}
						playerName={name}
						hasParents={hasParents}
						wasSent={wasReviewSent}
						disabled={!hasReviewText}
					/>
				</div>
			)}
		</div>
	);
};
